import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { SearchInput } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import type { Medal, MedalSearchResult } from "../api/types";

/**
 * Round 30 point 5 — Send Medal. Server-authorized and audited
 * (routes/admin/send-medal.js, super_admin only, every grant writes a
 * real admin_audit_log row) — this page is a thin UI over that route,
 * same as every other admin page; there's no privileged logic here.
 */
export function SendMedalPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MedalSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MedalSearchResult | null>(null);
  const [heldMedalIds, setHeldMedalIds] = useState<Set<number>>(new Set());
  const [medals, setMedals] = useState<Medal[]>([]);
  const [sendingMedalId, setSendingMedalId] = useState<number | null>(null);
  const [notifyUser, setNotifyUser] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<Medal[]>("/admin/medals").then((rows) => setMedals(rows.filter((m) => m.is_active))).catch(() => {});
  }, []);

  async function search() {
    if (!query.trim()) return;
    setIsSearching(true);
    setResults([]);
    try {
      const rows = await apiRequest<MedalSearchResult[]>("/admin/send-medal/search", { params: { query: query.trim() } });
      setResults(rows);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Search failed", "error");
    } finally {
      setIsSearching(false);
    }
  }

  async function selectUser(user: MedalSearchResult) {
    setSelectedUser(user);
    setResults([]);
    try {
      const history = await apiRequest<Array<{ medal_id: number }>>("/admin/send-medal/history", { params: { user_id: user.id } });
      setHeldMedalIds(new Set(history.map((h) => h.medal_id)));
    } catch {
      setHeldMedalIds(new Set());
    }
  }

  async function send(medal: Medal) {
    if (!selectedUser) return;
    setSendingMedalId(medal.id);
    try {
      const result = await apiRequest<{ alreadyHeld: boolean }>("/admin/send-medal", {
        method: "POST",
        body: { user_id: selectedUser.id, medal_id: medal.id, notifyUser },
      });
      if (result.alreadyHeld) {
        showToast(`${selectedUser.username} already has ${medal.name}`, "success");
      } else {
        showToast(`Sent ${medal.name} to ${selectedUser.username}`, "success");
        setHeldMedalIds((prev) => new Set(prev).add(medal.id));
      }
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not send medal", "error");
    } finally {
      setSendingMedalId(null);
    }
  }

  return (
    <AppShell title="Send Medal">
      <p className="mb-4 max-w-lg text-xs text-ink-faint">
        Search by username or public ID (original or the assigned unique one), pick a real user, then send any
        active medal. Sending a medal a user already has is a safe no-op — it's never duplicated.
      </p>

      <div className="mb-4 flex items-end gap-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Username or public ID..." />
        <Button variant="secondary" onClick={search} isLoading={isSearching} disabled={!query.trim()}>Search</Button>
      </div>

      {results.length > 0 && (
        <Card className="mb-4 divide-y divide-surface-border">
          {results.map((user) => (
            <button
              key={user.id}
              onClick={() => selectUser(user)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-raised"
            >
              <Avatar src={user.avatar_url} className="h-8 w-8 rounded-full object-cover" />
              <div>
                <p className="text-sm font-medium text-ink">{user.username}</p>
                <p className="text-xs text-ink-faint">ID: {user.user_id}</p>
              </div>
            </button>
          ))}
        </Card>
      )}

      {selectedUser && (
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-3">
            <Avatar src={selectedUser.avatar_url} className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="text-sm font-semibold text-ink">{selectedUser.username}</p>
              <p className="text-xs text-ink-faint">ID: {selectedUser.user_id}</p>
            </div>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelectedUser(null)}>Change user</Button>
          </div>

          <label className="mb-3 flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} />
            Notify user (System Sender message) — {notifyUser ? "ON" : "OFF: delivered silently"}
          </label>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {medals.map((medal) => {
              const held = heldMedalIds.has(medal.id);
              return (
                <div key={medal.id} className="flex flex-col items-center gap-2 rounded-lg border border-surface-border p-3 text-center">
                  {medal.png_asset_url ? <img src={medal.png_asset_url} alt={medal.name} className="h-12 w-12 object-contain" /> : <div className="h-12 w-12 rounded-md bg-surface-raised" />}
                  <p className="text-xs font-medium text-ink">{medal.name}</p>
                  <Button
                    size="sm"
                    variant={held ? "secondary" : "primary"}
                    isLoading={sendingMedalId === medal.id}
                    onClick={() => send(medal)}
                    disabled={held}
                  >
                    {held ? "Already Has" : "Send"}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </AppShell>
  );
}
